import type { PaginationState } from '@tanstack/vue-table'
import { toValue, unref } from 'vue'
import { isBoolean as _isBoolean, isNil as _isNil } from 'lodash-es'
import type { Schemas } from '@@/api/types.js'

// filter out any values that are not capitalized
type FilterCapitalized<K extends string> = K extends Capitalize<K> ? K : never

// resources in the schemas are Capitalized e.g. PatientLocation, User
// so filter out any which are not capitalized
type Resources = {
  [K in FilterCapitalized<keyof Schemas>]: Schemas[K]
}

export type IncludedResourceMap<TResources extends keyof Resources> = {
  [K in TResources]: Record<string, Resources[K]>
}

/**
 * helper for creating a lookup of resources included in the response,
 * where the keys are the resource type and the values are a map of id to resource
 *
 * @example
 * const response = {
 *   included: [
 *     { type: 'Consultation', id: '1', attributes: { ... } },
 *   ]
 * }
 *
 * buildIncludedResourceMap(response, ['Consultation', 'Patients'])
 *
 * // returns
 * {
 *   Consultation: {
 *     1: { type: 'Consultation', id: '1', attributes: { ... } }
 *   },
 *   Patients: {}
 * }
 */
export function buildIncludedResourceMap<T extends keyof Resources>(
  response: {
    data?: Schemas['include'] | Schemas['include'][]
    included?: Schemas['include'][]
  },
  resourceTypes: T[],
): IncludedResourceMap<T> {
  const { included = [], data } = response
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const resourceMap = {} as IncludedResourceMap<any>

  // a resource can appear in the included lists, or the main data, but never in both.
  // so we need to merge the two together to give us the full list of resources
  const allResources = data ? included.concat(data) : included

  // ensure all types of interest are included in the map
  const typesOfInterest = new Set<string>(resourceTypes)
  typesOfInterest.forEach((type) => (resourceMap[type] = {}))

  for (const item of allResources) {
    if (typesOfInterest.has(item.type)) {
      resourceMap[item.type][item.id] = item
    }
  }

  return resourceMap
}

export function toPaginationQueryParams(
  pagination: MaybeRef<Partial<PaginationState>>,
) {
  const { pageIndex, pageSize } = unref(pagination)
  return {
    'page[size]': pageSize,
    'page[number]': !_isNil(pageIndex) ? Math.max(0, pageIndex) + 1 : undefined,
  }
}

type ResourceIdentifier = { type: string; id: string }
type WithRelationships = {
  relationships?: Record<string, Schemas['reltomany'] | Schemas['reltoone']>
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type One<T extends keyof Resources = any> = { cardinality: 'one'; type: T }
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Many<T extends keyof Resources = any> = { cardinality: 'many'; type: T }

// maps a JSON:API relationship type to a One or Many relationship
type RelToOneMany<TRel> = TRel extends Schemas['reltoone']
  ? One
  : TRel extends Schemas['reltomany']
    ? Many
    : never

export type RelationshipDefs<T extends WithRelationships> = {
  [K in keyof NonNullable<T['relationships']>]?: RelToOneMany<
    NonNullable<T['relationships']>[K]
  >
}

type RelationshipRecord = Record<string, One | Many>

// the output of the builder function
type BuiltRelationships<T extends RelationshipRecord> = {
  [K in keyof T]: T[K] extends Many
    ? Resources[T[K]['type']][]
    : Resources[T[K]['type']] | undefined
}

// the input of the builder function
type ExpectedResource<T extends RelationshipRecord> = {
  relationships?: {
    [K in keyof T]?: T[K] extends Many
      ? Schemas['reltomany']
      : Schemas['reltoone']
  }
}

export const one = <T extends keyof Resources>(type: T): One<T> => {
  return { cardinality: 'one', type }
}
export const many = <T extends keyof Resources>(type: T): Many<T> => {
  return { cardinality: 'many', type }
}

/**
 * Type guard that checks whether a value is a JSON:API resource identifier
 * (an object with `type` and `id` string properties).
 *
 * @param value - The value to check.
 * @returns `true` if `value` is a `ResourceIdentifier`, narrowing its type accordingly.
 *
 * @example
 * isResourceIdentifier({ type: 'User', id: '1' }) // true
 *
 * @example
 * isResourceIdentifier(null) // false
 *
 * @example
 * isResourceIdentifier({ foo: 'bar' }) // false
 */
export const isResourceIdentifier = (
  value: unknown,
): value is ResourceIdentifier =>
  !!value && typeof value === 'object' && 'type' in value && 'id' in value

/**
 * Safely extracts the `id` from a to-one relationship's `data` field.
 *
 * JSON:API to-one relationship data is typed as `{} | { id, type } | null`,
 * so accessing `.id` directly requires narrowing. This helper does that.
 *
 * @example
 * const clientId = getRelationshipId(patient.relationships?.client?.data)
 */
export function getRelationshipId(
  data: Schemas['relationshipToOne'] | undefined,
): string | undefined {
  return isResourceIdentifier(data) ? data.id : undefined
}

const isKeyof = <T extends object>(obj: T, key: PropertyKey): key is keyof T =>
  key in obj

type RelationshipBuilder<T extends RelationshipRecord> = (
  resource: ExpectedResource<T>,
  included: Partial<IncludedResourceMap<T[keyof T]['type']>>,
) => BuiltRelationships<T>

/**
 * Handles joining up a Resource's relationships with included resources.
 * This is useful to make it easier to work with the JSON:API response data.
 *
 * @param defs An object defining a Resource's relationships, their type, and cardinality
 *
 * @example
 *
 * const taskResponse = await fetchTask()
 * const included = buildIncludedResourceMap(response, ['Patient', 'Client'])
 *
 * const buildRelationships = createBuilder({
 *  client: one('Client'),
 *  patients: many('Patient'),
 * })
 *
 * const relationships = buildRelationships(response.data, included)
 * AssertType<typeof relationships, {
 *   client?: Client,
 *   patients: Patient[],
 * }>
 */
export function createRelationshipBuilder<T extends RelationshipRecord>(
  defs: T,
): RelationshipBuilder<T> {
  return function builder(resource, included) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const output = {} as BuiltRelationships<any>

    if (!resource.relationships) {
      return output
    }

    for (const name in defs) {
      const def = defs[name]
      const type = def.type
      const rel = resource.relationships[name]?.data

      if (!isKeyof(included, type)) {
        continue
      }

      if (def.cardinality === 'many') {
        output[name] = []

        if (Array.isArray(rel)) {
          for (const item of rel) {
            if (item.type === type) {
              output[name].push(included[type]?.[item.id] ?? item)
            }
          }
        }
      } else if (isResourceIdentifier(rel)) {
        if (rel.type === type) {
          output[name] = included[type]?.[rel.id] ?? rel
        }
      }
    }

    return output
  }
}

/**
 * Conditionally sets `home_department` on a JSON:API relationships object,
 * respecting the three-state sentinel used by department select fields:
 *
 * - `null` / `undefined` — skip, BE will auto assign the current active department.
 * But with our current implementation in NFE, this case never happen, unless bug.
 * - `''`    — user explicitly cleared the field → sets `{ data: null }` to unset the relationship
 * - `"<id>"` — user selected a department → sets `{ data: { type, id } }`
 */
export function setHomeDepartmentRelationship(
  relationships: Record<string, unknown>,
  departmentId: string | null | undefined,
): void {
  if (departmentId === null || departmentId === undefined) {
    return
  }
  relationships['home_department'] = {
    data: departmentId ? { type: 'Department', id: departmentId } : null,
  }
}

export type QueryPaginationMeta = {
  count?: number
  page?: number
  pages?: number
}

type FilterValue = MaybeRef<string | string[] | boolean | undefined>

/**
 * Build `filter[…]` query params from a map of filter names to values.
 *
 * Automatically handles each value type:
 * - `string`    → trimmed; empty strings become `undefined` (omitted)
 * - `string[]`  → 1 value: `filter[name]=v`, N values: `filter[name.in]=a,b,c`
 * - `boolean`   → `filter[name]=true|false`
 * - `undefined`  → omitted
 *
 * All values accept `MaybeRef` so refs can be passed directly.
 *
 * @example
 * toFilterParams({
 *   search: filters?.search,
 *   item_type: filters?.itemType,
 *   archived: true,
 * })
 * // → { 'filter[search]': 'aspirin', 'filter[item_type]': 'MEDICINE', 'filter[archived]': true }
 */
export function toFilterParams(
  filters: Record<string, FilterValue>,
): Record<string, string | boolean | undefined> {
  const result: Record<string, string | boolean | undefined> = {}

  for (const [name, rawValue] of Object.entries(filters)) {
    const value = toValue(rawValue)

    if (value === undefined) {
      continue
    }

    if (_isBoolean(value)) {
      result[`filter[${name}]`] = value
      continue
    }

    if (Array.isArray(value)) {
      const arr = value.map((v) => v.trim()).filter(Boolean)
      if (arr.length === 1) {
        result[`filter[${name}]`] = arr[0]
      } else if (arr.length > 1) {
        result[`filter[${name}.in]`] = arr.join(',')
      }
      continue
    }

    // string
    const trimmed = value.trim()
    if (trimmed) {
      result[`filter[${name}]`] = trimmed
    }
  }

  return result
}
